"use client";

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { MultiSelect } from "@/components/ui/multi-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { JobProfile } from "../types";
import {
  languageLevels,
  driversLicenses,
  furtherEducations,
  positions
} from "../data";

interface JobProfileModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (
    profile: Omit<JobProfile, "id" | "company_id" | "created_at" | "updated_at">
  ) => void;
  profile?: JobProfile;
  title: string;
}

export function JobProfileModal({
  open,
  onOpenChange,
  onSave,
  profile,
  title
}: JobProfileModalProps) {
  const [selectedPosition, setSelectedPosition] = useState<string>("");
  const [selectedLanguageLevels, setSelectedLanguageLevels] = useState<
    string[]
  >([]);
  const [selectedDriversLicenses, setSelectedDriversLicenses] = useState<
    string[]
  >([]);
  const [selectedFurtherEducations, setSelectedFurtherEducations] = useState<
    string[]
  >([]);

  useEffect(() => {
    if (profile) {
      setSelectedPosition(profile.position || "");
      setSelectedLanguageLevels(profile.language_levels);
      setSelectedDriversLicenses(profile.drivers_licenses);
      setSelectedFurtherEducations(profile.further_educations);
    } else {
      setSelectedPosition("");
      setSelectedLanguageLevels([]);
      setSelectedDriversLicenses([]);
      setSelectedFurtherEducations([]);
    }
  }, [profile, open]);

  const handleSave = () => {
    onSave({
      position: selectedPosition,
      language_levels: selectedLanguageLevels,
      drivers_licenses: selectedDriversLicenses,
      further_educations: selectedFurtherEducations
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {profile
              ? "Bearbeite die Profilinformationen"
              : "Füge ein neues Profil hinzu"}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="position">Position</Label>
            <Select
              value={selectedPosition}
              onValueChange={setSelectedPosition}
            >
              <SelectTrigger id="position">
                <SelectValue placeholder="Position auswählen" />
              </SelectTrigger>
              <SelectContent>
                {positions.map((position) => (
                  <SelectItem key={position.value} value={position.value}>
                    {position.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="languageLevels">Sprachkenntnisse</Label>
            <MultiSelect
              options={languageLevels}
              selectedValues={selectedLanguageLevels}
              onValueChange={setSelectedLanguageLevels}
              placeholder="Sprachkenntnisse auswählen"
              maxCount={6}
              modalPopover={true}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="driversLicenses">Führerschein</Label>
            <MultiSelect
              options={driversLicenses}
              selectedValues={selectedDriversLicenses}
              onValueChange={setSelectedDriversLicenses}
              placeholder="Führerschein auswählen"
              maxCount={3}
              modalPopover={true}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="furtherEducations">Weiterbildungen</Label>
            <MultiSelect
              options={furtherEducations}
              selectedValues={selectedFurtherEducations}
              onValueChange={setSelectedFurtherEducations}
              placeholder="Weiterbildungen auswählen"
              maxCount={5}
              modalPopover={true}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button onClick={handleSave}>Speichern</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
